import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { AccountRefSchema } from '../../users/account-ref/account-ref';
import { ReportedCurrencySchema } from '../currency/currency';
import { EntitlementStoreSchema } from '../entitlement-store/entitlement-store';
import { MoneyAmountSchema } from '../money/money';
import { PaymentKindSchema } from '../payment-kind/payment-kind';
import { PaymentSourceSchema } from '../payment-source/payment-source';
import { PaymentStatusSchema } from '../payment-status/payment-status';
import { ProductKeySchema } from '../product-key/product-key';
import { ProductKindSchema } from '../product-kind/product-kind';

export const PAYMENT_NOTE_MAX_LENGTH = 500;
export const PAYMENT_EXTERNAL_REF_MAX_LENGTH = 255;

/**
 * One money movement, from any channel (P31).
 *
 * The **money view** beside the access tables — `user_entitlements` says what a
 * member holds, `fee_payments` whether a consumable was paid, and this says what
 * money did and when. Linked to them by id, never a replacement: nothing gates on
 * a payment (N14) and no member ever reads one (M11).
 *
 * `externalRef` is the idempotency handle per door — the store's own
 * `transaction_id`, the direct order's id — and `(store, externalRef)` is unique,
 * so the webhook, the member sync and the backfill can each report a movement
 * and one row lands. `amount`/`currency` are what the door **reported**, never
 * the catalogue price; the member sync reports none.
 *
 * `userId` is nullable because a payment outlives the account it came from: a
 * hard-deleted member's rows keep the id set null and the product key, no PII.
 */
export const PaymentSchema = z
  .object({
    id: UuidSchema,
    userId: UuidSchema.nullable(),

    /** Snapshot at the time of the movement — a withdrawn product must not un-record a charge. */
    productKey: ProductKeySchema,
    productKind: ProductKindSchema,
    store: EntitlementStoreSchema,
    kind: PaymentKindSchema,
    status: PaymentStatusSchema,

    amount: MoneyAmountSchema.nullable(),
    currency: ReportedCurrencySchema.nullable(),
    externalRef: z.string().min(1).max(PAYMENT_EXTERNAL_REF_MAX_LENGTH),
    source: PaymentSourceSchema,
    /** Which door reported it and under what circumstances — a Test Store purchase, a member sync. */
    note: z.string().max(PAYMENT_NOTE_MAX_LENGTH).nullable(),

    /** When the money moved, as the door reported it; the row's own creation time is `createdAt`. */
    occurredAt: z.iso.datetime(),
    refundedAt: z.iso.datetime().nullable(),

    /** The links to the access tables and the raw delivery — each `null` when that door was not involved. */
    billingEventId: UuidSchema.nullable(),
    directOrderId: UuidSchema.nullable(),
    feePaymentId: UuidSchema.nullable(),
    entitlementId: UuidSchema.nullable(),

    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type Payment = z.infer<typeof PaymentSchema>;

/**
 * A ledger row as the staff list and detail draw it: the payment, plus the member
 * who paid. A separate schema rather than a `member` field on the base, the shape
 * `DirectPaymentOrderDetailSchema` keeps (P22b4). `null` is ordinary — the account
 * is gone, or the row's `userId` was never resolvable.
 */
export const PaymentDetailSchema = PaymentSchema.extend({
  member: AccountRefSchema.nullable(),
}).strict();

export type PaymentDetail = z.infer<typeof PaymentDetailSchema>;
